import type { CookieAttributes } from "js-cookie";
import Cookies from "js-cookie";

import { logIfDebug } from "./logging";
import { XSRF_COOKIE_NAME } from "api-v2";

export interface ExtendedCookieAttributes extends CookieAttributes {
	keepCookie?: boolean;
}

interface KeepCookie {
	kc: boolean;
}

interface KeepCookieValue extends KeepCookie {
	val: string;
}

function isKeepCookieValue(obj: any): obj is KeepCookieValue {
	if (!obj || typeof obj !== "object") {
		return false;
	}

	const keys = Object.keys(obj);
	const allowedKeys: (keyof KeepCookieValue)[] = ["val", "kc"];

	// Check all keys are allowed
	if (
		!keys.every((key) => allowedKeys.includes(key as keyof KeepCookieValue))
	) {
		return false;
	}

	// Check required key types
	const hasValidTypes =
		typeof obj.value === "string" && typeof obj.keepCookie === "boolean";

	return hasValidTypes;
}

function hasKeepCookie(cookieValue: string): boolean {
	if (!cookieValue) {
		return false;
	}

	const obj = JSON.tryParse(cookieValue) as KeepCookie;

	return obj && typeof obj.kc === "boolean";
}

const defaultCookieOptions = {
	expires: 1,
	secure: true,
	sameSite: "strict",
} as CookieAttributes;

const setCookie = (
	name: string,
	value: string | object,
	options: ExtendedCookieAttributes = defaultCookieOptions
) => {
	try {
		options = { ...defaultCookieOptions, ...options };
		let cookieValue = value;
		let isObject = value instanceof Object;

		if (options.keepCookie) {
			if (isObject && value !== null) {
				cookieValue = { ...(value as object), keepCookie: true };
			} else {
				cookieValue = { val: value, kc: true } as KeepCookie;
				isObject = true;
			}
		}

		if (isObject) {
			cookieValue = JSON.stringify(cookieValue);
		}

		Cookies.set(name, cookieValue, options);
		// console.log(`Cookie '${name}' set successfully`);
	} catch (error) {
		// console.error(`Error setting cookie '${name}':`, error);
	}
};

const getCookieV2 = (name): string | object => {
	try {
		const value = Cookies.get(name);
		if (value === undefined) {
			// console.log(`Cookie '${name}' not found`);
			return null;
		}

		try {
			const cookieValue = JSON.tryParse<object>(value) ?? value;

			if (isKeepCookieValue(cookieValue)) {
				return cookieValue.val;
			}

			return cookieValue;
		} catch (e) {
			logIfDebug("Cookie Failed to load", e);
		}

		// console.log(`Cookie '${name}' retrieved successfully`);
		return value;
	} catch (error) {
		// console.error(`Error getting cookie '${name}':`, error);
		return null;
	}
};

const getCookie = (name): string | object => {
	try {
		const value = Cookies.get(name);
		if (value === undefined) {
			// console.log(`Cookie '${name}' not found`);
			return null;
		}
		// console.log(`Cookie '${name}' retrieved successfully`);
		return value;
	} catch (error) {
		// console.error(`Error getting cookie '${name}':`, error);
		return null;
	}
};

const removeCookie = (name, options = {}) => {
	try {
		Cookies.remove(name, options);
		// console.log(`Cookie '${name}' removed successfully`);
	} catch (error) {
		// console.error(`Error removing cookie '${name}':`, error);
	}
};

const removeAllCookie = () => {
	Object.keys(Cookies.get()).forEach((cookieName) => {
		const cookie = Cookies.get(cookieName);

		if (cookie === XSRF_COOKIE_NAME || hasKeepCookie(cookie)) {
			return;
		}

		Cookies.remove(cookieName);
	});
};

// const internalExports = {};
// if (process.env.NODE_ENV === "test") {
// 	// @ts-ignore
// 	module.exports.isKeepCookieValue = isKeepCookieValue;
// }

export { getCookie, getCookieV2, removeAllCookie, removeCookie, setCookie };
